/**
 * The site's route table, expressed so that edge middleware can tell a real
 * page from a path that does not exist without loading the Next.js router.
 *
 * `src/lib/agent/routes.test.ts` asserts this stays in sync with `src/pages`,
 * so adding a page without listing it here fails the unit tests.
 */

export interface SiteRoute {
  /** Path as served, or a prefix when `prefix` is true. */
  path: string;
  /** Match `path` and everything beneath it (catch-all routes). */
  prefix?: boolean;
  /** Listed in sitemap.xml and llms.txt. */
  public?: boolean;
  /** Requires a signed-in user, so it has no useful crawlable content. */
  authenticated?: boolean;
  title: string;
  description: string;
}

export const SITE_ROUTES: readonly SiteRoute[] = [
  {
    path: "/",
    public: true,
    title: "Setlist — chord sheets for the whole band",
    description:
      "Product overview: Nashville Number System chord charts, instant transpose, realtime multiplayer editing, arrangements, metronome and drone pad.",
  },
  {
    path: "/login",
    public: true,
    title: "Sign in or create a Setlist account",
    description:
      "Email, Google and Apple sign-in. Creating an account is free and unlocks the shared song library.",
  },
  {
    path: "/setlist",
    prefix: true,
    authenticated: true,
    title: "Setlist app",
    description:
      "The signed-in app: setlists, songs, chord sheets and live collaboration.",
  },
  {
    path: "/drone",
    authenticated: true,
    title: "Drone pad",
    description: "Standalone drone pad for holding a key between songs.",
  },
  {
    path: "/admin",
    authenticated: true,
    title: "Admin",
    description: "Internal administration tools.",
  },
  {
    path: "/auth/callback",
    authenticated: true,
    title: "Authentication callback",
    description: "OAuth redirect target. Not meant to be visited directly.",
  },
];

/** Machine-readable files that are always served as-is. */
export const MACHINE_READABLE_FILES = [
  {
    path: "/llms.txt",
    title: "llms.txt",
    description: "Machine-readable summary of the site for language models.",
  },
  {
    path: "/sitemap.xml",
    title: "sitemap.xml",
    description: "Every indexable URL.",
  },
  {
    path: "/robots.txt",
    title: "robots.txt",
    description: "Crawler policy and sitemap pointer.",
  },
] as const;

export const PUBLIC_ROUTES = SITE_ROUTES.filter((route) => route.public);

/** True when `pathname` is served by a real page (as opposed to a 404). */
export function isKnownRoute(pathname: string): boolean {
  const path = normalisePath(pathname);

  if (MACHINE_READABLE_FILES.some((file) => file.path === path)) return true;

  return SITE_ROUTES.some((route) =>
    route.prefix
      ? path === route.path || path.startsWith(`${route.path}/`)
      : path === route.path,
  );
}

/** Strip a trailing slash so `/login/` and `/login` are the same route. */
export function normalisePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.replace(/\/+$/, "") || "/";
  }
  return pathname;
}
